import i18next from "i18next";
import Cookies from "js-cookie";
import { decode } from "next-auth/jwt";

import { fallbackLng } from "@/app/i18n/settings";
import type { TokenInterface } from "@/libs/client/dexter/auth/authDexterServiceInterfaces/getTokenInterface";
import type { LocaleEnum } from "@/types/utils/locale";
import { appendFormData } from "@/utils/common/mapHelper/mapHelperUtils";
import dayjs from "@/utils/dayjs";
import { CookiesItemEnum } from "@/utils/enums/CookiesItemEnum";
import { GotherSideEnum } from "@/utils/enums/FeaturePathGlobalEnum";

import { getCookies, setCookies } from "../cookiesHelper/CookiesUtills";
import { convertToBoolean } from "../formatHelper/booleanUtills";
import { generateJWT } from "../other/easyEncodeDecode";
import { appendCacheParamsToUrl } from "../routeHelper/routeUrlUtills";
import {
  checkExistValue,
  checkExistValueAll,
} from "../validateHelper/validateUtils";
import { apiServerRequest } from "./serviceApiServerUtils";

export const decodeNextAuthClientSide = async (): Promise<any> => {
  const cookies = process.env.NEXT_PUBLIC_NEXTAUTH_URL?.startsWith("https://")
    ? Cookies.get("__Secure-next-auth.session-token")
    : Cookies.get("next-auth.session-token");

  try {
    const cookieValue = await decode({
      token: cookies,
      secret: process.env.NEXT_PUBLIC_JWT_SECRET ?? "",
    });

    return cookieValue;
  } catch (error) {
    return undefined;
  }
};

export async function checkAndGenerateActiveTokenGoApi(
  isGetNewToken: boolean = false,
): Promise<TokenInterface> {
  try {
    const bearerToken = getCookies(CookiesItemEnum.BEARER_TOKEN);
    const isDesktop = convertToBoolean(getCookies(CookiesItemEnum.IS_DESKTOP));

    const utmId = getCookies(CookiesItemEnum.COOKIE_UTM_ID);
    const tokenNextAuth = await decodeNextAuthClientSide();

    const device = isDesktop ? "web-view" : "mobile-view";

    const accountId = tokenNextAuth?.id;

    const userIpAddress = getCookies(CookiesItemEnum.USER_IP_ADDRESS);
    const gaClientId = getCookies(CookiesItemEnum.GA_CLIENT_ID);

    const gaSessionId = process.env.NEXT_PUBLIC_GA_ID
      ? Cookies.get(
          `${CookiesItemEnum.GA_CLIENT_ID}_${
            process.env.NEXT_PUBLIC_GA_ID.split("-")[1]
          }`,
        )
      : null;

    const noGenToken: boolean = checkExistValue(bearerToken) && !isGetNewToken;

    if (noGenToken) {
      return {
        token: bearerToken,
        isNewToken: false,
      } as TokenInterface;
    }

    const baseUrl: string = process.env.NEXT_PUBLIC_BASE_URL!;

    const urlAuthToken = `${baseUrl}/auth/token`;

    const formData = new FormData();
    formData.append("requestKey", process.env.NEXT_PUBLIC_REQUEST_KEY!);

    const response = await fetch(
      appendCacheParamsToUrl({
        url: urlAuthToken,
        minuteCache: 15,
        utmId,
      }),
      {
        method: "POST",
        body: formData,
        cache: "force-cache",
        headers: {
          ...(accountId && { "account-id": accountId }),
          ...(utmId && { "utm-id": utmId }),
          ...(gaClientId && { "ga-client-id": `${gaClientId}` }),
          ...(gaSessionId && { "ga-session-id": `${gaSessionId}` }),
          ...(userIpAddress && { "user-ip-address": `${userIpAddress}` }),
          device,
          side: GotherSideEnum.CLIENT,
        },
      },
    );

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const tokenObject = (await response.json()) as TokenInterface;

    const expireCookieMinuteTime = 30;

    const expireDate = dayjs().add(expireCookieMinuteTime, "minute");

    setCookies(
      CookiesItemEnum.BEARER_TOKEN,
      tokenObject.token,
      undefined,
      expireDate,
    );

    return {
      ...tokenObject,
      expireCookieMinute: expireCookieMinuteTime,
      isNewToken: true,
    } as TokenInterface;
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(
        "Unable to access client GoApi token:",
        (error && error.message) || "",
      );
    } else {
      // eslint-disable-next-line no-console
      console.error("Unable to access client GoApi token");
    }

    return {
      token: "",
      expire: "",
      isNewToken: false,
    };
  }
}

export async function checkAndGenerateActiveTokenPaymentApi(
  isGetNewToken: boolean = false,
): Promise<TokenInterface> {
  try {
    const bearerTokenPayment = getCookies(CookiesItemEnum.BEARER_TOKEN_PAYMENT);
    const bearerTokenExpirePayment = getCookies(
      CookiesItemEnum.BEARER_TOKEN_EXPIRE_PAYMENT,
    );

    const isHaveToSetNewToken: boolean =
      !checkExistValue(bearerTokenPayment) ||
      !checkExistValue(bearerTokenExpirePayment) ||
      (checkExistValue(bearerTokenExpirePayment) &&
        dayjs().isAfter(
          dayjs(bearerTokenExpirePayment!).subtract({
            minutes: 30,
          }),
        )) ||
      isGetNewToken === true;

    if (!isHaveToSetNewToken) {
      return {
        token: bearerTokenPayment,
        expire: bearerTokenExpirePayment,
        isNewToken: false,
      } as TokenInterface;
    }

    const baseUrl: string = process.env.NEXT_PUBLIC_PAYMENT_BASE_URL!;

    const urlAuthToken = `${baseUrl}/auth/token`;

    const formData = new FormData();
    formData.append("requestor_key", process.env.NEXT_PUBLIC_PAYMENT_KEY!);

    const response = await fetch(urlAuthToken, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const tokenObject = (await response.json()) as TokenInterface;

    setCookies(CookiesItemEnum.BEARER_TOKEN_PAYMENT, tokenObject.token);
    setCookies(CookiesItemEnum.BEARER_TOKEN_EXPIRE_PAYMENT, tokenObject.expire);

    return { ...tokenObject, isNewToken: true };
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(
        "Unable to access client Payment token:",
        (error && error.message) || "",
      );
    } else {
      // eslint-disable-next-line no-console
      console.error("Unable to access client Payment token");
    }

    return {
      token: "",
      expire: "",
      isNewToken: false,
    };
  }
}

export async function checkAndGenerateActiveTokenDexterApi(
  isGenNewToken: boolean = false,
): Promise<TokenInterface> {
  try {
    const bearerTokenDexter = getCookies(CookiesItemEnum.BEARER_TOKEN_DEXTER);
    const bearerTokenExpireDexter = getCookies(
      CookiesItemEnum.BEARER_TOKEN_EXPIRE_DEXTER,
    );

    const isHaveToSetNewToken: boolean =
      !checkExistValue(bearerTokenDexter) ||
      !checkExistValue(bearerTokenExpireDexter) ||
      (checkExistValue(bearerTokenExpireDexter) &&
        dayjs().isAfter(
          dayjs(bearerTokenExpireDexter!).subtract({
            minutes: 30,
          }),
        )) ||
      isGenNewToken === true;

    if (!isHaveToSetNewToken) {
      return {
        token: bearerTokenDexter,
        expire: bearerTokenExpireDexter,
      } as TokenInterface;
    }

    const baseUrl: string = process.env.NEXT_PUBLIC_DEXTER_BASE_URL!;

    const urlAuthToken = `${baseUrl}/auth/token`;

    const formData = new FormData();
    formData.append("requestKey", process.env.NEXT_PUBLIC_REQUEST_KEY!);

    const response = await fetch(urlAuthToken, {
      method: "POST",
      body: formData,
    });

    if (!response.ok) {
      throw new Error(`HTTP error! Status: ${response.status}`);
    }

    const tokenObject = (await response.json()) as TokenInterface;

    setCookies(CookiesItemEnum.BEARER_TOKEN_DEXTER, tokenObject.token);
    setCookies(CookiesItemEnum.BEARER_TOKEN_EXPIRE_DEXTER, tokenObject.expire);

    return { ...tokenObject, isNewToken: true };
  } catch (error) {
    if (error instanceof Error) {
      // eslint-disable-next-line no-console
      console.error(
        "Unable to access client Dexter token:",
        (error && error.message) || "",
      );
    } else {
      // eslint-disable-next-line no-console
      console.error("Unable to access client Dexter token");
    }

    return {
      token: "",
      expire: "",
      isNewToken: false,
    };
  }
}

// Helper function to get token
async function getAuthTokenForHeader(
  isDexter: boolean,
  isPayment: boolean,
  isGetNewToken: boolean,
  token?: string,
): Promise<string> {
  if (token) return token;

  if (isDexter) {
    return (await checkAndGenerateActiveTokenDexterApi(isGetNewToken)).token;
  }
  if (isPayment) {
    return (await checkAndGenerateActiveTokenPaymentApi(isGetNewToken)).token;
  }
  return (await checkAndGenerateActiveTokenGoApi(isGetNewToken)).token;
}

export async function setHeader(
  isDexter: boolean = false,
  isPayment: boolean = false,
  token?: string,
  sseEnable: boolean = false,
  langCustom?: string,
  isGetNewToken: boolean = false,
  accountId?: string,
  { showIsCorp }: { showIsCorp?: boolean } = { showIsCorp: false },
): Promise<HeadersInit> {
  // Language Determination
  const langCookie = getCookies(
    CookiesItemEnum.COOKIE_I18N_NEXT_LANG_NAME,
  )?.split("-")[0];
  const langI18n = i18next.languages?.[0]?.split("-")[0];
  const lang = langCustom || langI18n || langCookie || "en";

  // Token Authentication Handling
  const tokenAuth = await getAuthTokenForHeader(
    isDexter,
    isPayment,
    isGetNewToken,
    token,
  );

  const tokenNextAuth = await decodeNextAuthClientSide();

  // Retrieve Cookies
  const utmId = getCookies(CookiesItemEnum.COOKIE_UTM_ID);
  const accountUserId = accountId ?? tokenNextAuth?.id;
  const loyaltyLevel = tokenNextAuth?.loyaltyLevel;
  const partnerTokenId = getCookies(CookiesItemEnum.COOKIE_PARTNER_TOKEN_ID);
  const userIpAddress = getCookies(CookiesItemEnum.USER_IP_ADDRESS);
  const currency = getCookies(CookiesItemEnum.PREFERRED_CURRENCY) || "THB";
  const gaClientId = getCookies(CookiesItemEnum.GA_CLIENT_ID);
  const userCountry = getCookies(CookiesItemEnum.USER_COUNTRY);
  const gaSessionId = process.env.NEXT_PUBLIC_GA_ID
    ? Cookies.get(
        `${CookiesItemEnum.GA_CLIENT_ID}_${process.env.NEXT_PUBLIC_GA_ID.split("-")[1]}`,
      )
    : null;
  const isDesktop = convertToBoolean(getCookies(CookiesItemEnum.IS_DESKTOP));
  const device = isDesktop ? "web-view" : "mobile-view";

  // Extract the part between '.s' and the first '$'
  const gaSessionIdMatch = gaSessionId ? gaSessionId.match(/\.s([^$]+)/) : null;

  const gaSessionIdMatchNumber = gaSessionIdMatch ? gaSessionIdMatch[1] : null;

  // JWT Data Preparation
  const dataToken = {
    "account-id": accountUserId || null,
    "utm-id": utmId || null,
    "utm-ref": partnerTokenId || null,
    ip: userIpAddress || null,
    "user-country": userCountry || null,
    "loyalty-level": loyaltyLevel || null,
  };

  const dataTokenJwt = await generateJWT(dataToken);

  // Header Construction
  const header: HeadersInit = {
    "accept-language": lang,
    authorization: `Bearer ${tokenAuth}`,
    device,
    side: GotherSideEnum.CLIENT,
    ...(currency && { "preferred-currency": currency }),
    ...(accountId && { "account-id": accountUserId }),
    ...(utmId && { "utm-id": utmId }),
    ...(partnerTokenId && { "utm-ref": partnerTokenId }),
    ...(sseEnable && { "sse-enable": `${sseEnable}` }),
    ...(dataTokenJwt && { "data-token": `${dataTokenJwt}` }),
    ...(userIpAddress && { "user-ip-address": `${userIpAddress}` }),
    ...(gaClientId && { "ga-client-id": `${gaClientId}` }),
    ...(gaSessionIdMatchNumber && {
      "ga-session-id": `${gaSessionIdMatchNumber}`,
    }),
    ...(tokenNextAuth &&
      tokenNextAuth.isCorporate &&
      showIsCorp && {
        isCorp:
          tokenNextAuth && tokenNextAuth.isCorporate
            ? tokenNextAuth.isCorporate
            : undefined,
      }),
  };

  return header;
}

export const convertHeadersInitToObj = (
  headersInit: HeadersInit,
): { [name: string]: string } => {
  const headers: { [name: string]: string } = {};

  if (headersInit instanceof Headers) {
    headersInit.forEach((value, name) => {
      headers[name] = value;
    });
  } else if (Array.isArray(headersInit)) {
    for (const [name, value] of headersInit) {
      headers[name] = value;
    }
  } else if (typeof headersInit === "object") {
    return headersInit as { [name: string]: string };
  } else {
    throw new Error("Unsupported type for headersInit");
  }

  return headers;
};

async function apiGetRequest(
  url: string,
  token: string,
  isDexter: boolean,
  isPayment: boolean,
  logErrorMessage: boolean = false,
  langCustom?: string,
  isCache: boolean = true,
): Promise<Response> {
  async function getValueFormGoApi(isGetNewToken: boolean = false) {
    const langCookie = getCookies(CookiesItemEnum.COOKIE_I18N_NEXT_LANG_NAME);

    const lang = langCustom || langCookie?.split("-")[0] || ("en" as string);

    const currency = getCookies(CookiesItemEnum.PREFERRED_CURRENCY); // check language from url if not found set default to 'en';

    const bearerToken = getCookies(CookiesItemEnum.BEARER_TOKEN); // check language from url if not found set default to 'en'

    const utmId = getCookies(CookiesItemEnum.COOKIE_UTM_ID); // check language from url if not found set default to 'en'

    const headerApi = await setHeader(
      isPayment,
      isDexter,
      token,
      false,
      lang,
      isGetNewToken,
      undefined,
      { showIsCorp: true },
    );

    const { isCorp, ...header } = headerApi as Record<string, string>;

    const response = await fetch(
      appendCacheParamsToUrl({
        url,
        lang: langCookie ? (langCookie as LocaleEnum) : fallbackLng,
        currency: checkExistValueAll(currency) ? currency : "THB",
        minuteCache: 120,
        utmId,
        dataTokenJwt:
          convertToBoolean(isCorp) && checkExistValueAll(header["data-token"])
            ? header["data-token"]
            : undefined,
        isCache,
      }),
      {
        headers: header,
        method: "GET",
        cache:
          isCache && !convertToBoolean(isCorp) ? "force-cache" : "no-cache",
      },
    );

    if (response && response.status !== 200)
      throw new Error(
        `api error: ${response.status}, ${response.statusText || ""},
        url: ${url},
        header: ${header.toString() || ""}`,
      );

    return response.json();
  }

  try {
    const result = await getValueFormGoApi();

    return result;
  } catch (error: any) {
    const result = await getValueFormGoApi(true);

    return result;
  }
}

async function apiPostRequest(
  url: string,
  token: string,
  body: any,
  isDexter: boolean,
  isPayment: boolean,
  logErrorMessage: boolean = false,
  isThrowError: boolean = true,
): Promise<Response> {
  const accountId =
    checkExistValueAll(body) && checkExistValueAll(body.accountIdForce)
      ? String(body.accountIdForce)
      : undefined;

  const header = await setHeader(
    isPayment,
    isDexter,
    token,
    undefined,
    undefined,
    undefined,
    accountId,
  );
  const formData = new FormData();
  if (checkExistValue(body)) {
    appendFormData(formData, body);
  }

  if (isThrowError) {
    try {
      const response = await fetch(url, {
        headers: header,
        method: "POST",
        body: formData,
      });

      if (!response.ok && logErrorMessage === true) {
        throw new Error(
          `HTTP error! Status: ${response.status}, Message: ${
            (await response.json()).message
          }`,
        );
      }

      if (!response.ok) {
        throw new Error(`HTTP error! Status: ${response.status}`);
      }

      return await response.json();
    } catch (error: any) {
      throw error;
    }
  } else {
    const response = await fetch(url, {
      headers: header,
      method: "POST",
      body: formData,
    });

    const responseData = await response?.json();

    if (!response.ok || responseData?.status === false) {
      // eslint-disable-next-line @typescript-eslint/no-throw-literal
      throw `{"status": "${response.status}", "message": "${responseData?.message || false}", "code": "${responseData?.code || false}"}`;
    }

    return responseData;
  }
}

async function apiDeleteRequest(
  url: string,
  token: string,
  isDexter: boolean,
  isPayment: boolean,
  logErrorMessage: boolean = false,
): Promise<Response> {
  try {
    const header = await setHeader(isPayment, isDexter, token);

    const response = await fetch(url, {
      headers: header,
      method: "DELETE",
    });

    if (!response.ok && logErrorMessage === true) {
      throw new Error(
        `HTTP error! Status: ${response.status}, Message: ${
          (await response.json()).message
        }`,
      );
    }

    if (!response.ok) {
      throw new Error(
        `HTTP error! Status: ${response.status || ""}, Message: ${
          response.statusText || ""
        }`,
      );
    }

    return await response.json();
  } catch (error: any) {
    throw error;
  }
}

async function getAuthTokenForApi(
  baseUrl: string,
  isGenNewToken: boolean,
): Promise<string> {
  try {
    if (baseUrl.includes("dexter")) {
      const tokenDexterApi =
        await checkAndGenerateActiveTokenDexterApi(isGenNewToken);
      return tokenDexterApi.token;
    }
    if (baseUrl.includes("payment")) {
      const tokenPaymentApi =
        await checkAndGenerateActiveTokenPaymentApi(isGenNewToken);
      return tokenPaymentApi.token;
    }
    const tokenGoApi = await checkAndGenerateActiveTokenGoApi(isGenNewToken);
    return tokenGoApi.token;
  } catch (error) {
    if (baseUrl.includes("dexter")) {
      const tokenDexterApi = await checkAndGenerateActiveTokenDexterApi(true);
      return tokenDexterApi.token;
    }
    if (baseUrl.includes("payment")) {
      const tokenPaymentApi =
        await checkAndGenerateActiveTokenPaymentApi(isGenNewToken);
      return tokenPaymentApi.token;
    }
    const tokenGoApi = await checkAndGenerateActiveTokenGoApi(true);
    return tokenGoApi.token;
  }
}

export async function apiClientRequest(
  baseUrl: string,
  path: string,
  methodRequest: "GET" | "POST" | "DELETE",
  body?: any,
  logErrorMessage: boolean = false,
  langCustom?: string,
  isCache?: boolean,
  isGenNewToken: boolean = false,
  isThrowError: boolean = true,
): Promise<Response> {
  const isDexter = baseUrl.includes("dexter");
  const isPayment = baseUrl.includes("payment");

  const token = await getAuthTokenForApi(baseUrl, isGenNewToken);

  if (!token) {
    throw new Error("Token not found");
  }

  const url = `${baseUrl}/${path}`;

  switch (methodRequest) {
    case "GET":
      return apiGetRequest(
        url,
        token,
        isDexter,
        isPayment,
        logErrorMessage,
        langCustom,
        isCache,
      );
    case "POST":
      return apiPostRequest(
        url,
        token,
        body,
        isDexter,
        isPayment,
        logErrorMessage,
        isThrowError,
      );
    case "DELETE":
      return apiDeleteRequest(url, token, isDexter, isPayment, logErrorMessage);
    default:
      throw new Error("Method not supported");
  }
}

export async function apiRequest(
  baseUrl: string,
  path: string,
  methodRequest: "GET" | "POST" | "DELETE",
  body?: any,
  logErrorMessage: boolean = false,
  langCustom?: string,
  forceServerRequest: boolean = false,
  revalidate?: number,
  isCache?: boolean,
  isThrowError: boolean = true,
): Promise<Response> {
  try {
    // Server-side request if window is undefined or forceServerRequest is true
    if (typeof window === "undefined" || forceServerRequest) {
      let response = await apiServerRequest(
        baseUrl,
        path,
        methodRequest,
        body,
        logErrorMessage,
        langCustom,
        revalidate,
        undefined,
        isCache,
      );

      // Retry if the response is not valid
      if (
        !checkExistValueAll(response) ||
        (response as any)?.status === false
      ) {
        response = await apiServerRequest(
          baseUrl,
          path,
          methodRequest,
          body,
          logErrorMessage,
          langCustom,
          revalidate,
          true,
          isCache,
        );
      }

      return response;
    }

    // Client-side request
    return await apiClientRequest(
      baseUrl,
      path,
      methodRequest,
      body,
      logErrorMessage,
      langCustom,
      isCache,
      false,
      isThrowError,
    );
  } catch (error) {
    const isGenNewToken = true;

    // Retry logic for server-side requests
    if (typeof window === "undefined" || forceServerRequest) {
      return apiServerRequest(
        baseUrl,
        path,
        methodRequest,
        body,
        logErrorMessage,
        langCustom,
        revalidate,
        isGenNewToken,
      );
    }

    // Retry logic for client-side requests
    return apiClientRequest(
      baseUrl,
      path,
      methodRequest,
      body,
      logErrorMessage,
      langCustom,
      isCache,
      isGenNewToken,
      isThrowError,
    );
  }
}

export async function searchEngineApiRequest(
  baseURL: string,
  path: string,
): Promise<Response> {
  const request = await fetch(`${baseURL}/${path}`, {
    method: "GET",
  });

  return request.json();
}
